import type { Directive, DirectiveBinding } from 'vue'
import { h, render } from 'vue'
import { NGrid, NGridItem, NSkeleton } from 'naive-ui'

interface LoadingDirectiveValue {
  loading: boolean
  component?: any
}

// Custom skeleton layout component
const SkeletonLayout = {
  render() {
    return h('div', { class: 'skeleton-layout animate-pulse' }, [
      // Header skeleton
      h(NSkeleton, {
        class: 'my-4',
        style: {
          width: '100%',
          height: '24px',
        },
      }),
      // Content skeletons with two cards
      h(NGrid, { xGap: 16, cols: 2 }, () => [
        h(NGridItem, {}, () => [
          h('div', { class: 'card-skeleton mb-3' }, [
            // Card 1 Header
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '40px',
              },
            }),
            // Card 1 Content
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '100px',
              },
            }),
            // Card 1 Footer
            h(NSkeleton, {
              style: {
                width: '60%',
                height: '32px',
              },
            }),
          ]),
        ]),
        h(NGridItem, {}, () => [
          h('div', { class: 'card-skeleton mb-3' }, [
            // Card 2 Header
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '40px',
              },
            }),
            // Card 2 Content
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '100px',
              },
            }),
            // Card 2 Footer
            h(NSkeleton, {
              style: {
                width: '60%',
                height: '32px',
              },
            }),
          ]),
        ]),
        h(NGridItem, {}, () => [
          h('div', { class: 'card-skeleton mb-3' }, [
            // Card 3 Header
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '40px',
              },
            }),
            // Card 3 Content
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '100px',
              },
            }),
            // Card 3 Footer
            h(NSkeleton, {
              style: {
                width: '60%',
                height: '32px',
              },
            }),
          ]),
        ]),
        h(NGridItem, {}, () => [
          h('div', { class: 'card-skeleton mb-3' }, [
            // Card 4 Header
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '40px',
              },
            }),
            // Card 4 Content
            h(NSkeleton, {
              class: 'mb-3',
              style: {
                width: '100%',
                height: '100px',
              },
            }),
            // Card 4 Footer
            h(NSkeleton, {
              style: {
                width: '60%',
                height: '32px',
              },
            }),
          ]),
        ]),
      ]),
    ])
  },
}

export const loading: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<LoadingDirectiveValue>) {
    const value = binding.value || { loading: false }
    const isLoading = value.loading ?? false

    if (isLoading) {
      // Create a wrapper div for content
      const wrapper = document.createElement('div')
      wrapper.className = 'loading-wrapper'
      ;(wrapper as HTMLElement).style.cssText = 'position: relative; width: 100%; height: 100%; visibility: hidden;'

      // Move original content into wrapper
      while (el.firstChild)
        wrapper.appendChild(el.firstChild)

      // Create skeleton content
      const skeletonVNode = h(SkeletonLayout, {
        class: 'loading-skeleton',
        style: {
          width: '100%',
          height: '100%',
        },
      })

      // Create a container for skeleton
      const skeletonContainer = document.createElement('div')
      skeletonContainer.className = 'skeleton-container'
      ;(skeletonContainer as HTMLElement).style.cssText = 'position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: var(--n-body-color); z-index: 1000;'

      // Create a temporary container to render the skeleton
      const tempContainer = document.createElement('div')
      render(skeletonVNode, tempContainer)

      // Move skeleton content to container
      while (tempContainer.firstChild)
        skeletonContainer.appendChild(tempContainer.firstChild)

      // Add wrapper and skeleton container to element
      el.appendChild(wrapper)
      el.appendChild(skeletonContainer)
    }
  },
  updated(el: HTMLElement, binding: DirectiveBinding<LoadingDirectiveValue>) {
    const value = binding.value || { loading: false }
    const isLoading = value.loading ?? false

    let skeletonContainer = el.querySelector('.skeleton-container')
    const wrapper = el.querySelector('.loading-wrapper')

    if (isLoading && !skeletonContainer) {
      // Create a wrapper div for content
      const newWrapper = document.createElement('div')
      newWrapper.className = 'loading-wrapper'
      ;(newWrapper as HTMLElement).style.cssText = 'position: relative; width: 100%; height: 100%; visibility: hidden;'

      // Move original content into wrapper
      while (el.firstChild)
        newWrapper.appendChild(el.firstChild)

      // Create skeleton content
      const skeletonVNode = h(SkeletonLayout, {
        class: 'loading-skeleton',
        style: {
          width: '100%',
          height: '100%',
        },
      })

      // Create a container for skeleton
      skeletonContainer = document.createElement('div')
      skeletonContainer.className = 'skeleton-container'
      ;(skeletonContainer as HTMLElement).style.cssText = 'position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: var(--n-body-color); z-index: 1000;'

      // Create a temporary container to render the skeleton
      const tempContainer = document.createElement('div')
      render(skeletonVNode, tempContainer)

      // Move skeleton content to container
      while (tempContainer.firstChild)
        skeletonContainer.appendChild(tempContainer.firstChild)

      // Add wrapper and skeleton container to element
      el.appendChild(newWrapper)
      el.appendChild(skeletonContainer)
    }
    else if (!isLoading && skeletonContainer) {
      // Remove skeleton container
      skeletonContainer.remove()

      // Show and move content back from wrapper
      if (wrapper) {
        (wrapper as HTMLElement).style.visibility = 'visible'
        while (wrapper.firstChild)
          el.insertBefore(wrapper.firstChild, wrapper)

        wrapper.remove()
      }
    }
  },
}

// Plugin to easily install the directive
export const LoadingDirective = {
  install(app: any) {
    app.directive('loading', loading)
  },
}

export default LoadingDirective
